import TRAIcon from "../../../../design/icons/TRAIcon";
import { useNavigateBack } from "../../../../hooks/useNavigateBack";
import { useSystemBackButton } from "../../../../hooks/useSystemBackButton";
import { cn } from "../../../../design/utils/cn";
import c from "../../styles/profileClaude.module.css";

/**
 * Compact platform back chrome — back control + heading on one line.
 * Used by PlatformPageShell, competition organizer, profile/info drills.
 * Do not use for Arena session shell (excluded).
 * System / Android back matches the top-left back control when shown
 * (`useSystemBackButton`). Prefer this shell (or `useBackNavigation`) for any new Back UI.
 *
 * `variant="pill"` — legacy green-wash capsule (`headerVariant="pill"` only).
 * `variant="flat"` — canonical transparent pill · optional icon · green title (no header subtitle).
 * Typography: Geist + Geist Mono only (title display · subtitle body · labels mono in page body).
 *
 * @param {{
 *   title?: string;
 *   subtitle?: string;
 *   onOpenAccount?: () => void;
 *   onBack?: () => void;
 *   backFallback?: string;
 *   showBack?: boolean;
 *   end?: import("react").ReactNode;
 *   className?: string;
 *   variant?: "default" | "pill" | "flat";
 *   headerIcon?: string;
 * }} props
 */
export default function ProfileDashboardHeader({
  title,
  subtitle,
  onOpenAccount,
  onBack,
  backFallback = "/",
  showBack = true,
  end,
  className,
  variant = "default",
  headerIcon,
}) {
  const navigateBack = useNavigateBack(backFallback);
  const goBack = onBack || navigateBack;
  const handleBack = useSystemBackButton(goBack, showBack);
  const isPill = variant === "pill";
  const isFlat = variant === "flat";
  const trailing =
    end != null ? (
      end
    ) : onOpenAccount ? (
      <button
        type="button"
        className={c.iconBtn}
        aria-label="Account settings"
        onClick={onOpenAccount}
      >
        <TRAIcon name="settings" size="md" />
      </button>
    ) : isPill ? null : (
      <span className={c.topBarSpacer} aria-hidden />
    );

  const headerVariant = isFlat ? "flat" : isPill ? "pill" : "default";

  if (isFlat) {
    return (
      <header
        className={cn(c.tournamentHeroHeader, className)}
        data-testid="platform-back-header"
        data-variant="flat"
        data-tra-platform-header="flat"
        data-show-back={showBack ? "true" : "false"}
      >
        <div className={c.tournamentHeroCard}>
          <div className={c.tournamentHeroRow}>
            {showBack ? (
              <button
                type="button"
                className={c.tournamentHeroBack}
                aria-label="Back"
                onClick={handleBack}
              >
                <TRAIcon name="chevronLeft" size="md" />
              </button>
            ) : null}
            {headerIcon ? (
              <span className={c.tournamentHeroMark} aria-hidden>
                <TRAIcon name={headerIcon} size="md" />
              </span>
            ) : null}
            <div
              className={cn(
                c.tournamentHeroCopy,
                !headerIcon && c.tournamentHeroCopyFlush
              )}
            >
              <h1 className={c.tournamentHeroKicker}>{title || "Page"}</h1>
            </div>
            <span className={c.tournamentHeroFlex} aria-hidden />
            {trailing != null ? <div className={c.tournamentHeroActions}>{trailing}</div> : null}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={cn(c.topBar, isPill && c.topBarPill, className)}
      data-testid="platform-back-header"
      data-variant={headerVariant}
      data-show-back={showBack ? "true" : "false"}
    >
      <div className={c.topBarLeading}>
        {showBack ? (
          <button
            type="button"
            className={c.iconBtn}
            aria-label="Back"
            onClick={handleBack}
          >
            <TRAIcon name="chevronLeft" size="md" />
          </button>
        ) : null}
        {title || subtitle ? (
          <div className={c.topBarTitleStack}>
            {title ? (
              <h1 className={c.topBarTitle} data-tra-text-bold="page-title">
                {title}
              </h1>
            ) : null}
            {subtitle ? <p className={c.topBarSubtitle}>{subtitle}</p> : null}
          </div>
        ) : null}
      </div>
      {trailing != null ? <div className={c.topBarTrailing}>{trailing}</div> : null}
    </header>
  );
}
